import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Input } from "@realtyflows/ui";
import { getDocTemplate, createDocTemplate, updateDocTemplate, previewTemplate, getContractTemplate, updateContractTemplate } from "../api";
import { buyerStages, sellerStages } from "../data/mockData";
import useResponsive from "../hooks/useResponsive";

/* ═══════════════════════════════════════════════
   Field library — agents only see human labels
   ═══════════════════════════════════════════════ */

const FIELDS = {
  signature: [
    { raw: "\u00ABSIGN_1\u00BB", label: "Sign Here", signer: 1 },
    { raw: "\u00ABDATE_1\u00BB", label: "Date Signed", signer: 1 },
    { raw: "\u00ABNAME_1\u00BB", label: "Full Name", signer: 1 },
    { raw: "\u00ABINITIAL_1\u00BB", label: "Initials", signer: 1 },
    { raw: "\u00ABSIGN_2\u00BB", label: "Sign Here", signer: 2 },
    { raw: "\u00ABDATE_2\u00BB", label: "Date Signed", signer: 2 },
    { raw: "\u00ABNAME_2\u00BB", label: "Full Name", signer: 2 },
    { raw: "\u00ABINITIAL_2\u00BB", label: "Initials", signer: 2 },
  ],
  crm: [
    { raw: "{{buyer_name}}", label: "Buyer Name" },
    { raw: "{{buyer_email}}", label: "Buyer Email" },
    { raw: "{{buyer_phone}}", label: "Buyer Phone" },
    { raw: "{{seller_name}}", label: "Seller Name" },
    { raw: "{{agent_name}}", label: "Agent Name" },
    { raw: "{{agent_email}}", label: "Agent Email" },
    { raw: "{{brokerage_name}}", label: "Brokerage" },
    { raw: "{{property_address}}", label: "Property Address" },
    { raw: "{{price_formatted}}", label: "Price" },
    { raw: "{{geographic_area}}", label: "Geographic Area" },
    { raw: "{{compensation_terms}}", label: "Compensation" },
    { raw: "{{agreement_start_date}}", label: "Start Date" },
    { raw: "{{agreement_end_date}}", label: "End Date" },
    { raw: "{{close_date}}", label: "Close Date" },
    { raw: "{{today}}", label: "Today's Date" },
  ],
  fillable: [
    { raw: "\u00ABTEXT_1\u00BB", label: "Text Input", signer: 1 },
    { raw: "\u00ABADDR_1\u00BB", label: "Address", signer: 1 },
    { raw: "\u00ABPHONE_1\u00BB", label: "Phone", signer: 1 },
    { raw: "\u00ABCOMPANY_1\u00BB", label: "Company", signer: 1 },
    { raw: "\u00ABTEXT_2\u00BB", label: "Text Input", signer: 2 },
    { raw: "\u00ABADDR_2\u00BB", label: "Address", signer: 2 },
    { raw: "\u00ABPHONE_2\u00BB", label: "Phone", signer: 2 },
    { raw: "\u00ABCOMPANY_2\u00BB", label: "Company", signer: 2 },
  ],
};

/* ═══════════════════════════════════════════════
   Chip HTML — what appears inside the editor
   ═══════════════════════════════════════════════ */

const CHIP_STYLE = {
  signature: { bg: `${t.emerald}15`, bd: t.emerald, fg: t.emerald, bs: "solid" },
  crm:       { bg: `${t.sky}15`,     bd: t.sky,     fg: t.sky,     bs: "solid" },
  fillable:  { bg: "transparent",    bd: t.textMuted, fg: t.textSecondary, bs: "dashed" },
};

function buildChip(type, raw, label, signer) {
  const c = CHIP_STYLE[type];
  const tag = signer ? `${label} \u00B7 S${signer}` : label;
  return (
    `<span contenteditable="false" data-field="${raw}" data-type="${type}"` +
    ` style="display:inline-flex;align-items:center;gap:3px;` +
    `background:${c.bg};border:1.5px ${c.bs} ${c.bd};border-radius:4px;` +
    `padding:1px 6px 1px 8px;font-size:11px;color:${c.fg};font-weight:600;` +
    `white-space:nowrap;user-select:all;vertical-align:baseline;` +
    `font-family:sans-serif;cursor:default;margin:0 1px;line-height:20px;">` +
    `${tag}<span data-remove="1" style="cursor:pointer;font-size:9px;` +
    `opacity:0.4;margin-left:2px;line-height:1;" title="Remove">\u2715</span></span>`
  );
}

// Flat list for conversion
const ALL_FIELDS = [
  ...FIELDS.signature.map((f) => ({ ...f, type: "signature" })),
  ...FIELDS.crm.map((f) => ({ ...f, type: "crm" })),
  ...FIELDS.fillable.map((f) => ({ ...f, type: "fillable" })),
];

// DB body (raw tokens) → editor (visual chips)
function rawToChips(html, extraFields = []) {
  let out = html;
  const allWithExtra = [...ALL_FIELDS, ...extraFields];
  for (const f of allWithExtra) out = out.split(f.raw).join(buildChip(f.type, f.raw, f.label, f.signer));
  return out;
}

// Editor (visual chips) → DB body (raw tokens)
function chipsToRaw(html) {
  const el = document.createElement("div");
  el.innerHTML = html;
  el.querySelectorAll("[data-field]").forEach((n) => n.replaceWith(document.createTextNode(n.dataset.field)));
  return el.innerHTML;
}

/* ═══════════════════════════════════════════════
   Font sizes for the dropdown
   ═══════════════════════════════════════════════ */

const FONT_SIZES = [
  { label: "Small", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Medium", value: "4" },
  { label: "Large", value: "5" },
  { label: "X-Large", value: "6" },
];

/* ═══════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════ */

export default function TemplateEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isContractTemplate = searchParams.get("type") === "ai";
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const isCompact = responsive.isCompact;

  const [form, setForm] = useState(null);
  const [mode, setMode] = useState("edit"); // edit | fields | preview
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showSize, setShowSize] = useState(false);

  const editorRef = useRef(null);
  const cursorRef = useRef(null);
  const initHtmlRef = useRef(null);
  const sizeRef = useRef(null);

  const isNew = id === "new";

  /* ── Load template ── */
  const editorReady = useRef(false);
  const [customCrmFields, setCustomCrmFields] = useState([]);
  const backPath = isContractTemplate ? "/contracts/templates" : (isNew ? "/contracts/templates" : `/contracts/templates/${id}`);

  useEffect(() => {
    if (isNew) {
      // New template — start with blank editor
      setForm({ name: "", description: "", pipeline_type: "buyer", stage: "", trigger_type: "native" });
      initHtmlRef.current = `<h1 style="text-align:center">New Contract Template</h1>\n<p>Start editing your document here.</p>`;
      setLoading(false);
      return;
    }

    const loader = isContractTemplate ? getContractTemplate(id) : getDocTemplate(id);
    loader
      .then(async (tmpl) => {
        // Normalize contract template fields to match doc template format
        if (isContractTemplate) {
          tmpl.pipeline_type = tmpl.pipeline_type || "buyer";
          tmpl.stage = tmpl.stage || "";
          tmpl.trigger_type = tmpl.trigger_type || "native";
          tmpl.description = tmpl.description || "";
        }
        // Normalize agent_signer from legacy agent_is_signer_2
        if (tmpl.agent_signer === undefined || tmpl.agent_signer === null) {
          tmpl.agent_signer = tmpl.agent_is_signer_2 ? 2 : null;
        }
        setForm({ ...tmpl });

        // Build extra fields from custom_fields (doc templates) or placeholders (AI templates)
        let cfs = tmpl.custom_fields || [];
        if (isContractTemplate && Array.isArray(tmpl.placeholders)) {
          // Standard CRM keys already in the FIELDS library
          const standardKeys = new Set(FIELDS.crm.map(f => f.raw.replace(/\{\{|\}\}/g, "")));
          cfs = tmpl.placeholders
            .filter(p => !standardKeys.has(p.key))
            .map(p => ({ key: p.key, label: p.label || p.key, filled_by: p.filled_by }));
        }
        setCustomCrmFields(cfs);
        const extraFields = cfs.map(cf => ({
          raw: `{{${cf.key}}}`,
          label: cf.label,
          type: cf.filled_by === "signer_1" || cf.filled_by === "signer_2" ? "fillable" : "crm",
        }));
        if (tmpl.body) {
          // Extract inner body content (strip <html><head><style>...<body> wrapper)
          let bodyContent = tmpl.body;
          const bodyMatch = bodyContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          if (bodyMatch) bodyContent = bodyMatch[1].replace(/<div class="footer">[\s\S]*<\/div>\s*$/, "");
          initHtmlRef.current = rawToChips(bodyContent.trim(), extraFields);
        } else if (!isContractTemplate) {
          // Doc template without body — fetch generated preview
          try {
            const html = await previewTemplate(id);
            const match = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
            const inner = match ? match[1].replace(/<div class="footer">[\s\S]*<\/div>\s*$/, "") : "";
            initHtmlRef.current = rawToChips(inner.trim() || `<h1 style="text-align:center">${tmpl.name}</h1>\n<p>Start editing your document here.</p>`, extraFields);
          } catch (e) {
            initHtmlRef.current = rawToChips(`<h1 style="text-align:center">${tmpl.name}</h1>\n<p>Start editing your document here.</p>`, extraFields);
          }
        } else {
          // Contract template without body — generate from sections
          const sections = tmpl.content?.sections || [];
          let html = `<h1 style="text-align:center">${tmpl.content?.title || tmpl.name}</h1>\n`;
          for (const s of sections) {
            html += `<h2>${s.title}</h2>\n<p>${(s.content || "").replace(/\n/g, "<br>")}</p>\n`;
          }
          initHtmlRef.current = rawToChips(html.trim(), extraFields);
        }
      })
      .catch(() => navigate("/contracts/templates"))
      .finally(() => setLoading(false));
  }, [id, navigate, isNew, isContractTemplate]);

  /* ── Set editor HTML once (imperatively, so React never resets it) ── */
  useEffect(() => {
    if (!loading && initHtmlRef.current && editorRef.current && !editorReady.current) {
      editorRef.current.innerHTML = initHtmlRef.current;
      editorReady.current = true;
    }
  }, [loading]);

  /* ── Toast auto-dismiss ── */
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  /* ── Preview — show styled chips for fields ── */
  const loadPreview = useCallback(() => {
    if (!editorRef.current) return;
    // Convert chips back to raw tokens first (clean slate)
    let body = chipsToRaw(editorRef.current.innerHTML);
    // Replace raw tokens with styled preview chips
    const previewFields = [...ALL_FIELDS, ...customCrmFields.map(cf => ({ raw: `{{${cf.key}}}`, label: cf.label, type: "crm" }))];
    for (const f of previewFields) {
      const label = f.signer ? `${f.label} &middot; S${f.signer}` : f.label;
      const type = f.type;
      const cls = type === "signature" ? "chip-sig" : type === "crm" ? "chip-crm" : "chip-fill";
      const chip = `<span class="${cls}">${label}</span>`;
      body = body.split(f.raw).join(chip);
      // Also handle HTML-encoded guillemets
      const encoded = f.raw.replace(/\u00AB/g, "&laquo;").replace(/\u00BB/g, "&raquo;");
      if (encoded !== f.raw) body = body.split(encoded).join(chip);
    }
    const previewHtml = `<!DOCTYPE html>
<html><head><style>
  body { font-family: 'Georgia', serif; color: #1a1a2e; margin: 0; padding: 40px 50px; font-size: 11pt; line-height: 1.6; }
  h1 { font-size: 16pt; margin-bottom: 4px; }
  h2 { font-size: 12pt; margin: 24px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 8pt; color: #888; text-align: center; }
  .chip-sig { display:inline-flex; align-items:center; background:rgba(26,122,94,0.12); border:1.5px solid #1a7a5e; border-radius:4px; padding:1px 8px; font-size:11px; color:#1a7a5e; font-weight:600; white-space:nowrap; vertical-align:baseline; font-family:sans-serif; margin:0 1px; line-height:20px; }
  .chip-crm { display:inline-flex; align-items:center; background:rgba(59,130,246,0.12); border:1.5px solid #3b82f6; border-radius:4px; padding:1px 8px; font-size:11px; color:#3b82f6; font-weight:600; white-space:nowrap; vertical-align:baseline; font-family:sans-serif; margin:0 1px; line-height:20px; }
  .chip-fill { display:inline-flex; align-items:center; background:#f5f5f5; border:1.5px dashed #999; border-radius:4px; padding:1px 8px; font-size:11px; color:#666; font-weight:600; white-space:nowrap; vertical-align:baseline; font-family:sans-serif; margin:0 1px; line-height:20px; }
</style></head><body>
${body}
<div class="footer">Generated by RealtyFlows LLC</div>
</body></html>`;
    const blob = new Blob([previewHtml], { type: "text/html" });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === "preview") loadPreview();
  }, [mode, loadPreview]);

  /* ── Close font-size dropdown on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (sizeRef.current && !sizeRef.current.contains(e.target)) setShowSize(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Cursor: save on every selection change (works even when sidebar clicked) ── */
  const saveCursor = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // Only save if cursor is inside our editor
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      cursorRef.current = range.cloneRange();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", saveCursor);
    return () => document.removeEventListener("selectionchange", saveCursor);
  }, [saveCursor]);

  const restoreCursor = () => {
    const sel = window.getSelection();
    // Try restoring saved cursor
    if (cursorRef.current) {
      try {
        sel.removeAllRanges();
        sel.addRange(cursorRef.current);
        return;
      } catch (e) { /* stale range, fall through */ }
    }
    // Fallback: place cursor at end of editor
    const editor = editorRef.current;
    if (editor) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  /* ── Editor click: chip ✕ remove + cursor save ── */
  const handleEditorClick = (e) => {
    if (e.target.dataset.remove) {
      e.preventDefault();
      e.stopPropagation();
      const chip = e.target.closest("[data-field]");
      if (chip) chip.remove();
      return;
    }
    saveCursor();
  };

  /* ── Insert chip at cursor ── */
  const insertField = (chipHtml) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    restoreCursor();
    document.execCommand("insertHTML", false, chipHtml + "\u00A0");
    saveCursor();
  };

  /* ── Formatting commands ── */
  const exec = (cmd, val) => {
    editorRef.current?.focus();
    restoreCursor();
    document.execCommand(cmd, false, val || null);
    saveCursor();
  };

  /* ── Save ── */
  const handleSave = async () => {
    if (!form.name?.trim()) {
      setToast({ msg: "Template name is required", ok: false });
      return;
    }
    setSaving(true);
    try {
      const rawBody = editorRef.current ? chipsToRaw(editorRef.current.innerHTML) : form.body;
      const payload = {
        name: form.name, description: form.description,
        pipeline_type: form.pipeline_type, stage: form.stage,
        trigger_type: form.trigger_type, body: rawBody || null,
        agent_signer: form.agent_signer || null,
      };
      if (isContractTemplate) {
        await updateContractTemplate(form.id, payload);
        setToast({ msg: "Template saved", ok: true });
        setTimeout(() => navigate("/contracts/templates"), 800);
      } else if (isNew) {
        const created = await createDocTemplate(payload);
        setToast({ msg: "Template created", ok: true });
        setTimeout(() => navigate(`/contracts/templates/${created.id}`), 800);
      } else {
        await updateDocTemplate(form.id, payload);
        setToast({ msg: "Template saved", ok: true });
        setTimeout(() => navigate(`/contracts/templates/${id}`), 800);
      }
    } catch (err) {
      setToast({ msg: err.message || "Save failed", ok: false });
    } finally { setSaving(false); }
  };

  if (loading || !form) {
    return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  }

  /* ── Helpers ── */
  const stages = form.pipeline_type === "seller" ? sellerStages : buyerStages;

  const tbtn = (extra) => ({
    height: 28, borderRadius: 4, border: `1px solid ${t.border}`,
    background: t.surfaceWarm, cursor: "pointer", fontSize: 12,
    color: t.textPrimary, fontFamily: font.body,
    display: "flex", alignItems: "center", justifyContent: "center", ...extra,
  });

  const pill = (active, color = t.emerald) => ({
    padding: "4px 12px", borderRadius: 4, fontSize: 11, cursor: "pointer",
    background: active ? `${color}15` : t.surfaceWarm,
    border: `1px solid ${active ? color : t.border}`,
    color: active ? color : t.textSecondary,
    fontFamily: font.body, fontWeight: 600,
  });

  const subhead = (text) => (
    <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 4, marginTop: 8, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: font.body }}>{text}</div>
  );

  // Alignment icon — 3 bars with configurable alignment
  const AlignIcon = ({ align }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 1.5, width: 14, alignItems: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center" }}>
      <div style={{ width: 14, height: 1.5, background: "currentColor", borderRadius: 1 }} />
      <div style={{ width: 9, height: 1.5, background: "currentColor", borderRadius: 1 }} />
      <div style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1 }} />
    </div>
  );

  /* ── Drop handler: insert chip at exact drop position ── */
  const handleEditorDragOver = (e) => {
    // Show drop cursor at exact position while dragging
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    let range;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
    }
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const handleEditorDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let chipHtml = e.dataTransfer.getData("text/chip");
    if (!chipHtml) {
      // Fallback: browser may strip custom types, check if we can match by label
      const label = e.dataTransfer.getData("text/plain");
      if (!label) return;
      const match = ALL_FIELDS.find((f) => f.label === label);
      if (!match) return;
      chipHtml = buildChip(match.type, match.raw, match.label, match.signer);
    }
    const editor = editorRef.current;
    if (!editor) return;

    // If this is a move (existing chip), remove the source chip
    const moveId = e.dataTransfer.getData("text/move-id");
    if (moveId) {
      const src = editor.querySelector(`[data-drag-id="${moveId}"]`);
      if (src) src.remove();
    }

    // Place caret at drop coordinates
    let range;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
    }
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
    document.execCommand("insertHTML", false, chipHtml + "\u00A0");
    // Clean up drag IDs
    editor.querySelectorAll("[data-drag-id]").forEach((el) => el.removeAttribute("data-drag-id"));
    saveCursor();
  };

  /* ── Make existing chips in editor draggable ── */
  const handleEditorDragStart = (e) => {
    const chip = e.target.closest?.("[data-field]");
    if (!chip) return;
    const dragId = "drag-" + Date.now();
    chip.setAttribute("data-drag-id", dragId);
    e.dataTransfer.setData("text/chip", chip.outerHTML);
    e.dataTransfer.setData("text/move-id", dragId);
    e.dataTransfer.effectAllowed = "move";
  };

  // Sidebar field row — draggable + click to insert
  const FieldItem = ({ type, field }) => {
    const chipHtml = buildChip(type, field.raw, field.label, field.signer);
    const c = CHIP_STYLE[type];
    return (
      <div
        draggable="true"
        onDragStart={(e) => {
          e.dataTransfer.setData("text/chip", chipHtml);
          e.dataTransfer.setData("text/plain", field.label);
          e.dataTransfer.effectAllowed = "copyMove";
          // Use the chip span as drag image
          const span = e.currentTarget.querySelector("span");
          if (span) e.dataTransfer.setDragImage(span, 0, 0);
        }}
        onClick={() => insertField(chipHtml)}
        style={{ padding: "4px 8px", borderRadius: 6, cursor: "grab", transition: "background 0.12s" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = t.surfaceWarm)}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span style={{
          display: "inline-block", background: c.bs === "dashed" ? "transparent" : `${c.bg}`,
          border: `1.5px ${c.bs} ${c.bd}`, borderRadius: 4, padding: "2px 8px",
          fontSize: 11, color: c.fg, fontWeight: 600, whiteSpace: "nowrap", fontFamily: font.body,
          pointerEvents: "none",
        }}>
          {field.signer ? `${field.label} \u00B7 S${field.signer}` : field.label}
        </span>
      </div>
    );
  };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.ok ? t.emerald : t.coral,
          color: "#fff", padding: isMobile ? "8px 14px" : "10px 20px", borderRadius: 8,
          fontSize: isMobile ? 11 : 12, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          maxWidth: isMobile ? "calc(100vw - 40px)" : "auto",
        }}>{toast.msg}</div>
      )}

      {/* ── Top bar ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: isMobile ? 8 : 12,
        flexWrap: isMobile ? "wrap" : "nowrap",
        gap: isMobile ? 8 : 0,
      }}>
        <button onClick={() => navigate(backPath)}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: t.textSecondary, fontSize: isMobile ? 12 : 13, fontFamily: font.body, padding: 0 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = t.emerald)}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textSecondary)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          {isNew ? "Back to Templates" : "Back"}
        </button>

        {/* Mode toggle */}
        <div style={{
          display: "flex", gap: 2, background: t.surfaceWarm, borderRadius: 8, padding: 2,
          order: isMobile ? 3 : 0,
          width: isMobile ? "100%" : "auto",
        }}>
          {[["edit", "Edit Text"], ["fields", "Add Fields"], ["preview", "Preview"]].map(([key, label]) => (
            <button key={key} onClick={() => setMode(key)} style={{
              padding: isMobile ? "6px 10px" : "6px 16px", borderRadius: 6, border: "none", cursor: "pointer",
              background: mode === key ? t.surface : "transparent",
              color: mode === key ? t.textPrimary : t.textSecondary,
              fontWeight: mode === key ? 700 : 400, fontSize: isMobile ? 11 : 12, fontFamily: font.body,
              boxShadow: mode === key ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.15s",
              flex: isMobile ? 1 : "none",
            }}>{label}</button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outline" size="sm" onClick={() => navigate(backPath)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isNew ? "Create Template" : "Save"}
          </Button>
        </div>
      </div>

      {/* ── Settings bar ── */}
      {mode !== "preview" && (
        <div style={{
          display: "flex", gap: isMobile ? 10 : 16, alignItems: "center", flexWrap: "wrap",
          padding: isMobile ? "8px 12px" : "10px 16px", background: t.surface, border: `1px solid ${t.border}`,
          borderRadius: 10, marginBottom: isMobile ? 8 : 12,
        }}>
          <div style={{ flex: 1, minWidth: isMobile ? "100%" : 180 }}>
            <Input size="sm" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Template name" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body }}>Pipeline</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[["buyer", "Buyer"], ["seller", "Seller"]].map(([v, l]) => (
                <div key={v} onClick={() => setForm((p) => ({ ...p, pipeline_type: v, stage: (v === "seller" ? sellerStages : buyerStages)[0] }))}
                  style={pill(form.pipeline_type === v)}>{l}</div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body }}>Stage</span>
            <select value={form.stage || ""} onChange={(e) => setForm((p) => ({ ...p, stage: e.target.value }))}
              style={{ background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 4, padding: "4px 8px", fontSize: 11, fontFamily: font.body, color: t.textPrimary, outline: "none", cursor: "pointer" }}>
              <option value="">None</option>
              {stages.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body }}>E-Sign</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[["native", "Native"], ["manual", "Manual"]].map(([v, l]) => (
                <div key={v} onClick={() => setForm((p) => ({ ...p, trigger_type: v }))}
                  style={pill(form.trigger_type === v, t.sky)}>{l}</div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body }}>Agent Signs As</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[[null, "None"], [1, "S1"], [2, "S2"]].map(([v, l]) => (
                <div key={String(v)} onClick={() => setForm((p) => ({ ...p, agent_signer: v }))}
                  style={pill(form.agent_signer === v, t.coral)}>{l}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Main editor area (hidden during preview, keeps DOM alive) ── */}
      <div style={{
        display: mode === "preview" ? "none" : "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 8 : 12,
        alignItems: "stretch",
      }}>

        {/* ── Field sidebar (always rendered to keep editor DOM stable) ── */}
          <div style={{
            display: mode === "fields" ? "flex" : "none",
            flexDirection: "column", gap: 10,
            width: isMobile ? "100%" : 260,
            flexShrink: 0, overflowY: "auto",
            maxHeight: isMobile ? 300 : "none",
          }}>
            {/* Signature Fields */}
            <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 2, fontFamily: font.body }}>Signature Fields</div>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8, fontFamily: font.body, lineHeight: 1.4 }}>
                Click to place where signers sign, initial, or print their name.
              </div>
              {subhead("Signer 1")}
              {FIELDS.signature.filter((f) => f.signer === 1).map((f) => <FieldItem key={f.raw} type="signature" field={f} />)}
              {subhead("Signer 2")}
              {FIELDS.signature.filter((f) => f.signer === 2).map((f) => <FieldItem key={f.raw} type="signature" field={f} />)}
            </div>

            {/* Auto-filled from CRM */}
            <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 2, fontFamily: font.body }}>Auto-filled from CRM</div>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8, fontFamily: font.body, lineHeight: 1.4 }}>
                Pulls data from the deal record when sent. No typing needed.
              </div>
              {FIELDS.crm.map((f) => <FieldItem key={f.raw} type="crm" field={f} />)}
              {customCrmFields.filter(cf => cf.filled_by !== "signer_1" && cf.filled_by !== "signer_2").length > 0 && (
                <>
                  {subhead("Template Fields")}
                  {customCrmFields.filter(cf => cf.filled_by !== "signer_1" && cf.filled_by !== "signer_2").map((cf) => (
                    <FieldItem key={cf.key} type="crm" field={{ raw: `{{${cf.key}}}`, label: cf.label }} />
                  ))}
                </>
              )}
            </div>

            {/* Signer Fills In */}
            <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 2, fontFamily: font.body }}>Signer Fills In</div>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 8, fontFamily: font.body, lineHeight: 1.4 }}>
                Empty fields the signer types into during signing.
              </div>
              {subhead("Signer 1")}
              {FIELDS.fillable.filter((f) => f.signer === 1).map((f) => <FieldItem key={f.raw} type="fillable" field={f} />)}
              {customCrmFields.filter(cf => cf.filled_by === "signer_1").map((cf) => (
                <FieldItem key={cf.key} type="fillable" field={{ raw: `{{${cf.key}}}`, label: cf.label }} />
              ))}
              {subhead("Signer 2")}
              {FIELDS.fillable.filter((f) => f.signer === 2).map((f) => <FieldItem key={f.raw} type="fillable" field={f} />)}
              {customCrmFields.filter(cf => cf.filled_by === "signer_2").map((cf) => (
                <FieldItem key={cf.key} type="fillable" field={{ raw: `{{${cf.key}}}`, label: cf.label }} />
              ))}
            </div>
          </div>

        {/* ── Editor panel ── */}
        <div style={{ flex: 1, minWidth: 0, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {/* ── Formatting toolbar (Edit Text mode) ── */}
          {mode === "edit" && (
            <div style={{
              padding: isMobile ? "6px 8px" : "8px 12px",
              borderBottom: `1px solid ${t.border}`,
              display: "flex", gap: isMobile ? 3 : 4, alignItems: "center", flexWrap: "wrap",
              overflowX: isMobile ? "auto" : "visible",
              WebkitOverflowScrolling: "touch",
            }}>
              {/* Bold / Italic / Underline */}
              <button onClick={() => exec("bold")} style={tbtn({ width: 28, fontWeight: 700 })}>B</button>
              <button onClick={() => exec("italic")} style={tbtn({ width: 28, fontStyle: "italic" })}>I</button>
              <button onClick={() => exec("underline")} style={tbtn({ width: 28, textDecoration: "underline" })}>U</button>

              <div style={{ width: 1, height: 20, background: t.border, margin: "0 4px", flexShrink: 0 }} />

              {/* Font size */}
              <div ref={sizeRef} style={{ position: "relative" }}>
                <button onClick={() => setShowSize(!showSize)} style={tbtn({ padding: "0 8px", gap: 4, fontSize: 11 })}>
                  Size
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                {showSize && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 50,
                    background: t.surface, border: `1.5px solid ${t.border}`, borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", width: 110, overflow: "hidden",
                  }}>
                    {FONT_SIZES.map((s) => (
                      <div key={s.value}
                        onClick={() => { exec("fontSize", s.value); setShowSize(false); }}
                        style={{ padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: font.body, color: t.textPrimary }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = t.surfaceWarm)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >{s.label}</div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ width: 1, height: 20, background: t.border, margin: "0 4px", flexShrink: 0 }} />

              {/* Alignment */}
              <button onClick={() => exec("justifyLeft")} title="Align left" style={tbtn({ width: 28 })}><AlignIcon align="left" /></button>
              <button onClick={() => exec("justifyCenter")} title="Align center" style={tbtn({ width: 28 })}><AlignIcon align="center" /></button>
              <button onClick={() => exec("justifyRight")} title="Align right" style={tbtn({ width: 28 })}><AlignIcon align="right" /></button>

              <div style={{ width: 1, height: 20, background: t.border, margin: "0 4px", flexShrink: 0 }} />

              {/* Headings */}
              <button onClick={() => exec("formatBlock", "h1")} style={tbtn({ padding: "0 8px", fontSize: 11, fontWeight: 700 })}>H1</button>
              <button onClick={() => exec("formatBlock", "h2")} style={tbtn({ padding: "0 8px", fontSize: 11, fontWeight: 700 })}>H2</button>
              <button onClick={() => exec("formatBlock", "p")} style={tbtn({ padding: "0 8px", fontSize: 11 })}>P</button>

              <div style={{ width: 1, height: 20, background: t.border, margin: "0 4px", flexShrink: 0 }} />

              {/* Lists */}
              <button onClick={() => exec("insertUnorderedList")} style={tbtn({ padding: "0 8px", fontSize: 11 })}>List</button>
              <button onClick={() => exec("insertOrderedList")} style={tbtn({ padding: "0 8px", fontSize: 11 })}>1. List</button>

              <div style={{ width: 1, height: 20, background: t.border, margin: "0 4px", flexShrink: 0 }} />

              {/* HR */}
              <button onClick={() => exec("insertHorizontalRule")} style={tbtn({ padding: "0 8px", fontSize: 11 })}>&mdash; Line</button>
            </div>
          )}

          {/* ── contentEditable document ── */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onMouseUp={saveCursor}
            onKeyUp={saveCursor}
            onClick={handleEditorClick}
            onDragStart={handleEditorDragStart}
            onDragOver={handleEditorDragOver}
            onDrop={handleEditorDrop}
            style={{
              flex: 1, minHeight: isMobile ? 350 : 600,
              padding: isMobile ? "16px 14px" : isCompact ? "24px 28px" : "32px 40px",
              outline: "none", fontFamily: "'Georgia', serif",
              fontSize: isMobile ? "10pt" : "11pt", lineHeight: 1.7, color: "#1a1a2e",
              overflowY: "auto",
            }}
          />
        </div>
      </div>

      {/* ── Preview (shown when mode === preview) ── */}
      {mode === "preview" && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
          {previewUrl ? (
            <iframe src={previewUrl} title="Preview" style={{
              width: "100%", height: isMobile ? 400 : 700,
              border: "none", background: "#fff",
            }} />
          ) : (
            <div style={{ padding: isMobile ? 30 : 60, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>Loading preview...</div>
          )}
          <div style={{
            padding: isMobile ? "8px 12px" : "10px 20px",
            background: t.surfaceWarm, borderTop: `1px solid ${t.border}`,
            fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body,
          }}>
            Preview shows sample data. Save your changes first to see updates here.
          </div>
        </div>
      )}
    </div>
  );
}
